import React, { useEffect, useState } from "react";
import { Checkbox, Form, Image, Input } from "antd";
import TextArea from "antd/es/input/TextArea";
import { FiPrinter, FiSave, FiChevronDown, FiChevronUp, FiFileText } from "react-icons/fi";
import { message } from "antd";
import { calculateAge } from "../../../../utils/helpers";
import { buildPrintPhisoHTML } from "./PrintPhisoAssessment";

// ── Shared form body — rendered inside whichever accordion panel is open ─────
function AssessmentFormContent({
  assessmentForm,
  patientData,
  physioTherapyData,
  savingAssessment,
  onSave,
  setAssessmentApiState,
  setSavingAssessment,
  fetchUserReport,
}) {
  const handleSaveAssessment = async () => {
    setSavingAssessment(true);
    try {
      const f = assessmentForm.getFieldsValue();
      const assessmentFields = {
        diagnosis:           f.ptDiagnosis                       || "",
        pastHistory:         f.ptPastHistory                     || "",
        chiefComplain:       f.ptChiefComplain                   || "",
        jobAndOccupation:    f.ptJob                             || "",
        painScore:           Number(f.ptPainScore)               || 0,
        painDistribution:    f.ptPainDistribution                || "",
        lymphaticEdema:      f.ptLymphaticEdema?.includes("yes") ?? false,
        radioInvestigations: f.ptRadioInvestigations             || "",
        labNotes:            f.ptLabNotes?.includes("yes")       ?? false,
        labResults:          f.ptLabResults                      || "",
        precautions:         f.ptPrecautions                     || "",
        ptAssessment:        f.ptAssessment                      || "",
        ptPlanOfTreatment:   f.ptPlanOfTreatment                 || "",
        sessionsNum:         Number(f.ptSessionsNum?.[0])        || 0,
        progressNotes:       f.ptProgressNotes                   || "",
        tttGoals:            f.ptTttGoals                        || "",
        discharge:           f.ptDischarge                       || "",
        otherNotes:          f.ptOtherNotes                      || "",
      };
      setAssessmentApiState((prev) => ({ ...prev, ...assessmentFields }));
      await onSave(assessmentFields);
      await fetchUserReport?.();
      message.success("Assessment saved successfully");
    } catch {
      message.error("Failed to save assessment");
    } finally {
      setSavingAssessment(false);
    }
  };

  const handlePrintAssessment = () => {
    const formValues = assessmentForm.getFieldsValue();
    const html = buildPrintPhisoHTML({ formValues, patientData, physioTherapyData });
    const printWindow = window.open("", "_blank", "width=900,height=700");
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  return (
    <div className=" p-2 md:p-4 bg-white">

      {/* Action bar */}
      <div className="flex flex-wrap items-center justify-end gap-2 mb-6">
        <button
          type="button"
          onClick={handlePrintAssessment}
          className="flex items-center gap-2 bg-gray-100 text-gray-700 border border-gray-300
                     px-3 sm:px-5 py-2 rounded-lg hover:bg-gray-200 transition-colors font-semibold shadow-sm text-sm"
        >
          <FiPrinter size={16} />
        <span className="hidden md:block">  Print</span> 
        </button>
        <button
          type="button"
          onClick={handleSaveAssessment}
          disabled={savingAssessment}
          className="flex items-center gap-2 bg-primary text-white
                     px-3 sm:px-5 py-2 rounded-lg hover:bg-primary/90 transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow text-sm"
        >
          <FiSave size={16} />
           <span className="hidden md:block">  {savingAssessment ? "Saving…" : "Save Assessment"}</span>
        </button>
      </div>

      {/* Form */}
      <div className="font-serif">
        <div className="text-center mb-5 pb-3 border-b-2 border-gray-400">
          <h2 className="text-lg sm:text-xl font-bold underline tracking-wide text-gray-900 leading-tight">
            Physical therapy outpatient Assessment
          </h2>
        </div>

        <Form form={assessmentForm} layout="vertical">

          {/* Name / Age / Gender / Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-2
                          items-end pb-3 mb-3 border-b border-dashed border-gray-300">
            <Form.Item name="ptName" label={<span className="font-semibold text-sm">Name:</span>} className="sm:col-span-2 mb-0">
              <Input disabled className="border-0 border-b border-gray-500 rounded-none px-0 focus:shadow-none bg-transparent disabled:bg-white disabled:text-black" />
            </Form.Item>
            <Form.Item  name="ptAge" label={<span className="font-semibold text-sm">Age:</span>} className="mb-0">
              <Input disabled className="border-0 border-b border-gray-500 rounded-none px-0 text-center bg-transparent disabled:bg-white disabled:text-black" />
            </Form.Item>
            <div className="flex flex-wrap gap-x-4 gap-y-2 items-end">
              <Form.Item name="ptGender" label={<span className="font-semibold text-sm">Gender:</span>} className="mb-0">
                                <Checkbox.Group disabled className="flex gap-2">
                  <Checkbox value="M">M.</Checkbox>
                  <Checkbox value="F">F.</Checkbox>
                </Checkbox.Group>
              </Form.Item>
              <Form.Item name="ptDate" label={<span className="font-semibold text-sm">Date:</span>} className="mb-0">
                <Input disabled placeholder="\ \" className="border-0 border-b border-gray-500 rounded-none w-24 px-0 bg-transparent disabled:bg-white disabled:text-black" />
              </Form.Item>
            </div>
          </div>

          {/* Diagnosis / Phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2
                          items-end pb-3 mb-3 border-b border-dashed border-gray-300">
            <Form.Item name="ptDiagnosis" label={<span className="font-semibold text-sm">Diagnosis:</span>} className="mb-0">
              <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
            </Form.Item>
            <Form.Item name="ptPhone" label={<span className="font-semibold text-sm">Phone Num:</span>} className="mb-0">
              <Input disabled className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent disabled:bg-white disabled:text-black" />
            </Form.Item>
          </div>

          {/* Past History */}
          <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
            <Form.Item name="ptPastHistory" label={<span className="font-semibold text-sm">Past. History:</span>} className="mb-0">
              <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
            </Form.Item>
          </div>

          {/* Chief Complain */}
          <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
            <Form.Item name="ptChiefComplain" label={<span className="font-semibold text-sm">Chief Complain:</span>} className="mb-0">
              <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
            </Form.Item>
          </div>

          {/* Job & Occupation */}
          <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
            <Form.Item name="ptJob" label={<span className="font-semibold text-sm">Job & Occupation:</span>} className="mb-0">
              <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
            </Form.Item>
          </div>

          {/* Pain score / distribution / lymphatic edema */}
          <div className="flex flex-wrap gap-x-5 gap-y-3 items-end pb-3 mb-3 border-b border-dashed border-gray-300">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold whitespace-nowrap">Pain score:</span>
              <Form.Item name="ptPainScore" className="mb-0 w-12">
                <Input className="border-0 border-b border-gray-500 rounded-none px-0 text-center bg-transparent" />
              </Form.Item>
              <span className="text-sm font-semibold">/10</span>
            </div>
            <div className="flex items-center gap-2 flex-1 min-w-[140px]">
              <span className="text-sm font-semibold whitespace-nowrap">Pain distribution:</span>
              <Form.Item name="ptPainDistribution" className="mb-0 flex-1">
                <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
              </Form.Item>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold whitespace-nowrap">Lymphatic edema:</span>
              <Form.Item name="ptLymphaticEdema" className="mb-0">
                <Checkbox.Group className="flex gap-2">
                  <Checkbox value="yes">Yes</Checkbox>
                  <Checkbox value="no">No.</Checkbox>
                </Checkbox.Group>
              </Form.Item>
            </div>
          </div>

          {/* Radio Investigations */}
          <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
            <Form.Item name="ptRadioInvestigations" label={<span className="font-semibold text-sm">Radio Investigations:</span>} className="mb-0">
              <TextArea autoSize={{ minRows: 2 }} className="border-0 border-b border-gray-400 rounded-none resize-none bg-transparent focus:shadow-none" />
            </Form.Item>
          </div>

          {/* Lab Notes + Results */}
          <div className="flex flex-wrap gap-x-5 gap-y-3 items-end pb-3 mb-3 border-b border-dashed border-gray-300">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">Lab Notes:</span>
              <Form.Item name="ptLabNotes" className="mb-0">
                <Checkbox.Group className="flex gap-2">
                  <Checkbox value="yes">Yes</Checkbox>
                  <Checkbox value="no">No.</Checkbox>
                </Checkbox.Group>
              </Form.Item>
            </div>
            <div className="flex items-center gap-2 flex-1 min-w-[140px]">
              <span className="text-sm font-semibold whitespace-nowrap">Results:</span>
              <Form.Item name="ptLabResults" className="mb-0 flex-1">
                <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
              </Form.Item>
            </div>
          </div>

          {/* Precautions */}
          <div className="pb-2 mb-4 border-b border-dashed border-gray-300">
            <Form.Item name="ptPrecautions" label={<span className="font-semibold text-sm">Precautions:</span>} className="mb-0">
              <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
            </Form.Item>
          </div>

          {/* Body Diagram */}
          <div className="border border-gray-300 rounded-lg p-3 flex justify-center mb-4 bg-gray-50 overflow-hidden">
            <Image style={{ maxWidth: 500, height: "auto" }} src="/phisoImg.png" />
          </div>

          {/* Assessment table */}
          <div className="border border-gray-400 rounded-sm mb-4 overflow-hidden">
            <table className="w-full border-collapse text-sm">
              <tbody>
                {[
                  { name: "ptAssessment",      label: "PT Assessment",        rows: 3, placeholder: "Enter assessment details…" },
                  { name: "ptPlanOfTreatment", label: "PT Plan of Treatment", rows: 3, placeholder: "Enter treatment plan…" },
                ].map(({ name, label, rows, placeholder }) => (
                  <tr key={name} className="border-b border-gray-400">
                    <td className="border-r border-gray-400 p-2 font-semibold bg-gray-50
                                   w-28 sm:w-40 align-top whitespace-normal sm:whitespace-nowrap text-xs sm:text-sm">
                      {label}
                    </td>
                    <td className="p-0">
                      <Form.Item name={name} className="mb-0">
                        <TextArea autoSize={{ minRows: rows }} bordered={false} className="rounded-none resize-none text-sm" placeholder={placeholder} />
                      </Form.Item>
                    </td>
                  </tr>
                ))}

                <tr className="border-b border-gray-400">
                  <td className="border-r border-gray-400 p-2 font-semibold bg-gray-50
                                 w-28 sm:w-40 align-top whitespace-normal sm:whitespace-nowrap text-xs sm:text-sm">
                    Sessions Num:
                  </td>
                  <td className="p-3">
                    <Form.Item name="ptSessionsNum" className="mb-0">
                      <Checkbox.Group className="flex flex-wrap gap-3">
                        {["3", "6", "9", "12", "18", "24", "More"].map((v) => (
                          <Checkbox key={v} value={v}>{v}</Checkbox>
                        ))}
                      </Checkbox.Group>
                    </Form.Item>
                  </td>
                </tr>

                <tr>
                  <td className="border-r border-gray-400 p-2 font-semibold bg-gray-50
                                 w-28 sm:w-40 align-top whitespace-normal sm:whitespace-nowrap text-xs sm:text-sm">
                    Progress Notes
                  </td>
                  <td className="p-0">
                    <Form.Item name="ptProgressNotes" className="mb-0">
                      <TextArea autoSize={{ minRows: 2 }} bordered={false} className="rounded-none resize-none text-sm" />
                    </Form.Item>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* TTT Goals / Discharge / Other Notes */}
          <div className="space-y-2">
            {[
              { name: "ptTttGoals",   label: "TTT Goals:" },
              { name: "ptDischarge",  label: "Discharge:" },
              { name: "ptOtherNotes", label: "Any Other Notes:" },
            ].map(({ name, label }) => (
              <div key={name} className="flex flex-col xs:flex-row xs:items-center gap-1 xs:gap-2 pb-1 border-b border-dashed border-gray-300">
                <span className="text-sm font-semibold whitespace-nowrap">{label}</span>
                <Form.Item name={name} className="mb-0 flex-1">
                  <Input bordered={false} className="border-b border-gray-400 rounded-none px-0" />
                </Form.Item>
              </div>
            ))}
          </div>
        </Form>

        {/* PT Signatures */}
        <div className="flex   justify-between gap-6 sm:gap-0 pt-8 mt-4 border-t border-gray-300">
          <div className="text-center">
            <div className="w-40 border-b border-gray-500 h-8 mb-1 mx-auto" />
            <span className="text-sm font-semibold">P.T Specialist Signature</span>
          </div>
          <div className="text-center">
            <div className="w-40 border-b border-gray-500 h-8 mb-1 mx-auto" />
            <span className="text-sm font-semibold">Consultant Signature</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function AssessmentTab({
  patientData,
  assessmentApiState,
  setAssessmentApiState,
  assessment,
  setAssessment,
  savingAssessment,
  setSavingAssessment,
  assessmentForm,
  scheduleForm,
  onSave,
  physioTherapyData,
  reportCount,
  currentReportIndex,
  onReportIndexChange,
  fetchUserReport,
}) {
  const [expandedReport, setExpandedReport] = useState(currentReportIndex ?? 0);

  useEffect(() => {
    setExpandedReport(currentReportIndex ?? 0);
  }, [currentReportIndex]);

  useEffect(() => {
    assessmentForm.setFieldsValue({
      ptName:    (patientData?.patient.firstName + " " + patientData?.patient.lastName) || "",
      ptAge:     calculateAge(patientData?.patient.birthDay) || "",
      ptGender:  patientData?.patient.gender,
      ptDate:    new Date(patientData.bookingDate).toLocaleDateString(),
      ptPhone:   patientData?.patient?.phoneNumber,

      ptDiagnosis:           physioTherapyData?.diagnosis,
      ptPastHistory:         physioTherapyData?.pastHistory,
      ptChiefComplain:       physioTherapyData?.chiefComplain,
      ptJob:                 physioTherapyData?.jobAndOccupation,
      ptPainScore:           physioTherapyData?.painScore || "",
      ptPainDistribution:    physioTherapyData?.painDistribution,
      ptLymphaticEdema:      physioTherapyData?.lymphaticEdema ? "yes" : "no",
      ptRadioInvestigations: physioTherapyData?.radioInvestigations,
      ptLabNotes:            physioTherapyData?.labNotes ? "yes" : "no",
      ptLabResults:          physioTherapyData?.labResults,
      ptPrecautions:         physioTherapyData?.precautions,
      ptAssessment:          physioTherapyData?.ptAssessment,
      ptPlanOfTreatment:     physioTherapyData?.ptPlanOfTreatment,
      ptSessionsNum:         physioTherapyData?.sessionsNum
                               ? [String(physioTherapyData.sessionsNum)]
                               : [],
      ptProgressNotes:       physioTherapyData?.progressNotes,
      ptTttGoals:            physioTherapyData?.tttGoals,
      ptDischarge:           physioTherapyData?.discharge,
      ptOtherNotes:          physioTherapyData?.otherNotes,
    });
  }, [physioTherapyData]);

  const sharedFormProps = {
    assessmentForm,
    patientData,
    physioTherapyData,
    savingAssessment,
    onSave,
    setAssessmentApiState,
    setSavingAssessment,
    fetchUserReport,
  };

  const handleToggle = (index) => {
    if (expandedReport === index) {
      setExpandedReport(null);
    } else {
      setExpandedReport(index);
      onReportIndexChange?.(index);
    }
  };

  // ── Single report: no accordion needed ──
  if (!reportCount || reportCount <= 1) {
    return (
      <div className="w-full  mx-auto px-2 sm:px-4">
        <AssessmentFormContent {...sharedFormProps} />
      </div>
    );
  }

  // ── Multiple reports: accordion, form expands inline under each tab ──
  return (
    <div className="w-full  mx-auto px-2 sm:px-4">

      <div className="flex items-center gap-2 mb-3 px-1">
        <FiFileText size={15} className="text-primary" />
        <span className="text-xs font-bold uppercase tracking-widest text-primary/80">
          Reports ({reportCount})
        </span>
      </div>

      <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm divide-y divide-gray-100">
        {Array.from({ length: reportCount }, (_, i) => {
          const isOpen = expandedReport === i;
          const isCurrent = i === currentReportIndex;

          return (
            <div key={i}>
              {/* Tab header */}
              <button
                type="button"
                onClick={() => handleToggle(i)}
                className={`w-full flex items-center justify-between px-4 py-3 text-sm font-semibold transition-colors
                  ${isOpen
                    ? "bg-primary/10 text-primary"
                    : "bg-white hover:bg-gray-50 text-gray-700"
                  }`}
              >
                <span className="flex items-center gap-2">
                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold
                    ${isCurrent ? "bg-primary text-white" : "bg-gray-200 text-gray-600"}`}>
                    {i + 1}
                  </span>
                  Report {i + 1}
                  {/* {isCurrent && (
                    <span className="text-[10px] font-semibold bg-primary/15 text-primary px-2 py-0.5 rounded-full">
                      Current
                    </span>
                  )} */}
                </span>
                {isOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
              </button>

              {/* Form content — renders directly below the header when open */}
              {isOpen && (
                <div className="border-t border-primary/20">
                  <AssessmentFormContent {...sharedFormProps} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}